import { BookOpen02Icon, Download02Icon } from "@/icons";
import { Link } from "@/kit/ui/link";
import { useCallback, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { MetaLayout } from "../../common/MetaLayout";
import { guessCodeLanguage } from "../../utils/guessCodeLanguage";
import { useFetchText, useIsomorphicLayoutEffect } from "../../utils/hooks";
import CodeViewer from "../CodeViewer";
import Loading from "../Loading";
import Markdown from "../Markdown";
import PdfViewer from "../PdfViewer";
import {
  FileViewerFragment$data,
  FileViewerFragment$key,
} from "./__generated__/FileViewerFragment.graphql";
import CsvViewer from "./CsvViewer";
import PythonNotebookViewer from "./PythonNotebookViewer";

const FILE_VIEWER_MAX_SIZE = 10_000_000; // 10 MB

const FileViewerFragment = graphql`
  fragment FileViewerFragment on FileBrowserFileEntry {
    name
    contentType
    contentLength
    downloadUrl
  }
`;

interface Props {
  entry: FileViewerFragment$key;
  disableScroll?: boolean;
}

export default function FileViewer({
  entry: entryFragment,
  disableScroll = false,
}: Props) {
  const entry = useFragment(FileViewerFragment, entryFragment);
  const ref = useRef<HTMLDivElement>(null);
  const onLoad = useCallback(() => {
    if (ref.current && !disableScroll) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [ref, disableScroll]);

  return (
    <div ref={ref}>
      <div className="w-full p-3 mb-8  bg-white rounded-xl shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] outline-1 outline-offset-[-1px] outline-grey-200 inline-flex justify-between items-center gap-3">
        <div className="inline-flex items-center gap-2">
          <div className="size- p-2 bg-white rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] outline-1 outline-offset-[-1px] outline-grey-200 inline-flex justify-start items-center gap-2">
            <BookOpen02Icon className="size-6 text-grey-700" />
          </div>
          <p className="text-gray-800 text-base font-bold leading-6">
            {entry.name}
          </p>
        </div>
        <Link to={entry.downloadUrl} variant="secondary">
          <Download02Icon />
          <FormattedMessage defaultMessage="Download" />
        </Link>
      </div>
      {entry.contentLength < FILE_VIEWER_MAX_SIZE ? (
        <FileDisplay entry={entry} onLoad={onLoad} />
      ) : (
        <FileTooBig />
      )}
    </div>
  );
}

function FileTooBig() {
  return (
    <p className="text-center text-grey-500">
      <FormattedMessage defaultMessage="File too big" />
    </p>
  );
}

interface FileDisplayProps {
  entry: FileViewerFragment$data;
  onLoad: () => void;
}

function FileDisplay({
  entry: { contentType, name, downloadUrl },
  onLoad,
}: FileDisplayProps) {
  const { isLoading, data, isText, error, aborted } = useFetchText(downloadUrl);
  useIsomorphicLayoutEffect(() => {
    if (!isLoading) {
      onLoad();
    }
  }, [isLoading, onLoad]);
  if (aborted) {
    return null;
  }
  if (isLoading) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={
          {
            "--from-opacity": 0,
            "--to-opacity": 1,
            animation: "fade 0.2s ease-in",
          } as React.CSSProperties
        }
      >
        <div className="w-12">
          <Loading />
        </div>
      </div>
    );
  }
  if (error) {
    return error.toString();
  }

  if (data) {
    switch (contentType) {
      case "text/markdown":
        return <Markdown>{data}</Markdown>;

      case "text/csv":
        return <CsvViewer>{data}</CsvViewer>;

      case "application/vnd.jupyter":
        return <PythonNotebookViewer>{data}</PythonNotebookViewer>;

      case "application/pdf":
        return <PdfViewer data={data} />;

      default:
        if (isText) {
          return (
            <MetaLayout metaDescription={data}>
              <CodeViewer language={guessCodeLanguage(name) ?? "text"}>
                {data}
              </CodeViewer>
            </MetaLayout>
          );
        } else if (contentType.startsWith("image/")) {
          const imageSrc = `data:${contentType};base64,${data}`;
          return (
            <MetaLayout metaImageUrl={imageSrc} metaImageAlt={name}>
              <img src={`data:${contentType};base64,${data}`} alt={name} />
            </MetaLayout>
          );
        } else {
          return (
            <div className="text-center text-grey-500">
              <FormattedMessage defaultMessage="Cannot load binary file in browser" />
            </div>
          );
        }
    }
  }
}
